import { DefaultDateRangePickerInput } from "../../../../../../common/components/default_date_range_picker_input";
import { DefaultSelectInput } from "../../../../../../common/components/default_select_input";
import { MaskedInput } from "../../../../../../common/components/masked_inputs/masked_input";
import { useMemo } from "react";
import { FornecedorDropDown } from "../../../../../../common/domain/models/fornecedor_dropdown";
import { RequisicaoCompraDropDown } from "../../../../../../common/domain/models/requisicao_compra_dropdown";
import { ProdutoServicoDropDown } from "../../../../../../common/domain/models/produto_servico_dropdown";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { DefaultMultiAutocomplete } from "../../../../../../common/components/default_multi_autocomplete";
import { OrdemCompraDropDown } from "../../../../../../common/domain/models/ordem_compra_dropdown";

import CascateList from "../../../../../../common/components/cascate_list";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";

/**
 * Header
 */

type Props = {
  dataRequisicaoStart: Date | null;
  dataRequisicaoEnd: Date | null;
  fornecedor: number[] | null;
  requisicao: number[] | null;
  produtoServico: number[] | null;
  centroCusto: number[] | null;
  nroColeta: string | null;
  ordemCompra: number[] | null;
  status: number;
  fornecedoresDropdown: FornecedorDropDown[];
  requisicoesCompraDropdown: RequisicaoCompraDropDown[];
  produtoServicosDropdown: ProdutoServicoDropDown[];
  centroCustoDropdown: CentroCustoDropDown[];
  ordemComprasDropdown: OrdemCompraDropDown[];
  onDataRequisicaoChange: (de: Date | null, ate: Date | null) => void;
  onFornecedorChange: (fornecedor: number[] | null) => void;
  onRequisicaoChange: (requisicao: number[] | null) => void;
  onProdutoServicoChange: (produtoServico: number[] | null) => void;
  onCentroCustoChange: (centroCusto: number[] | null) => void;
  onNroColetaChange: (nroColeta: string | null) => void;
  onOrdemCompraChange: (ordemCompra: number[] | null) => void;
  onStatusChange: (status: number) => void;
  onClearFilters: () => void;
};

export const Header = ({
  dataRequisicaoStart,
  dataRequisicaoEnd,
  fornecedor,
  requisicao,
  produtoServico,
  centroCusto,
  nroColeta,
  ordemCompra,
  status,
  fornecedoresDropdown,
  requisicoesCompraDropdown,
  produtoServicosDropdown,
  centroCustoDropdown,
  ordemComprasDropdown,
  onDataRequisicaoChange,
  onFornecedorChange,
  onRequisicaoChange,
  onProdutoServicoChange,
  onCentroCustoChange,
  onNroColetaChange,
  onOrdemCompraChange,
  onStatusChange,
  onClearFilters,
}: Props) => {
  const selectedFornecedor = useMemo(() => {
    return fornecedoresDropdown.filter(x => fornecedor?.some(y => y === x.idFornecedor)) ?? null;
  }, [
    fornecedor,
    fornecedoresDropdown,
  ]);

  const selectedRequisicao = useMemo(() => {
    return requisicoesCompraDropdown.filter(x => requisicao?.some(y => y === x.idRequisicaoCompra)) ?? null;
  }, [
    requisicao,
    requisicoesCompraDropdown,
  ]);

  const selectedProdutoServico = useMemo(() => {
    return produtoServicosDropdown.filter(x => produtoServico?.some(y => y === x.idProdutoServico)) ?? null;
  }, [
    produtoServico,
    produtoServicosDropdown,
  ]);

  const selectedCentroCusto = useMemo(() => {
    return centroCustoDropdown.filter(x => centroCusto?.some(y => y === x.id)) ?? null;
  }, [
    centroCusto,
    centroCustoDropdown,
  ]);

  const selectedOrdemCompra = useMemo(() => {
    return ordemComprasDropdown.filter(x => ordemCompra?.some(y => y === x.idOrdemCompra)) ?? null;
  }, [
    ordemCompra,
    ordemComprasDropdown,
  ]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 5,
        label: 'Todas as coletas',
      },
      {
        id: 0,
        label: 'Em Elaboração',
      },
      {
        id: 1,
        label: 'Finalizadas',
      },
      {
        id: 3,
        label: 'Aprovadas',
      },
      {
        id: 4,
        label: 'Reprovadas',
      },
      {
        id: 2,
        label: 'Gerou Ordem de Compra',
      },
    ];
  }, [
  ]);

  return (
    <CascateList title='Filtros' defaultOpen={true}>
      <div className="w-full flex flex-col gap-4">
        <div className="w-full grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          <DefaultDateRangePickerInput
            label='Data da Requisição'
            dataDe={dataRequisicaoStart}
            dataAte={dataRequisicaoEnd}
            onChange={onDataRequisicaoChange}
          />

          <DefaultMultiAutocomplete
            label='Fornecedor'
            options={fornecedoresDropdown}
            getOptionKey={x => x.idFornecedor}
            getOptionLabel={x => x.nomeFantasia}
            value={selectedFornecedor}
            onChange={e => onFornecedorChange(e !== null ? e.map(x => x.idFornecedor) : null)}
          />

          <DefaultMultiAutocomplete
            label='Requisição'
            options={requisicoesCompraDropdown}
            getOptionKey={x => x.idRequisicaoCompra}
            getOptionLabel={x => x.descricao}
            value={selectedRequisicao}
            onChange={e => onRequisicaoChange(e !== null ? e.map(x => x.idRequisicaoCompra) : null)}
          />

          <DefaultMultiAutocomplete
            label='Produto/Serviço'
            options={produtoServicosDropdown}
            getOptionKey={x => x.idProdutoServico}
            getOptionLabel={x => x.descricao}
            value={selectedProdutoServico}
            onChange={e => onProdutoServicoChange(e !== null ? e.map(x => x.idProdutoServico) : null)}
          />

          <DefaultMultiAutocomplete
            label='Centro de Custo'
            options={centroCustoDropdown}
            getOptionKey={x => x.id}
            getOptionLabel={x => x.title}
            value={selectedCentroCusto}
            onChange={e => onCentroCustoChange(e !== null ? e.map(x => x.id) : null)}
          />

          <MaskedInput
            name='nroColeta'
            label='Nro da Coleta'
            mask='0000000000'
            maskPlaceholder='_________'
            value={nroColeta}
            onChange={onNroColetaChange}
          />

          <DefaultMultiAutocomplete
            label='Ordem de Compra'
            options={ordemComprasDropdown}
            getOptionKey={x => x.idOrdemCompra}
            getOptionLabel={x => x.descricao}
            value={selectedOrdemCompra}
            onChange={e => onOrdemCompraChange(e !== null ? e.map(x => x.idOrdemCompra) : null)}
          />

          <DefaultSelectInput
            name='status'
            label='Status'
            showSelectionLabel={false}
            options={statusOptions}
            value={status}
            onChangeValue={e => onStatusChange(Number(e))}
          />
        </div>

        <div className="w-full flex justify-end">
          <div className="w-[180px]">
            <PrimaryButton
              startIcon={<DynamicIcon nameIcon="ClearRounded" size={25} className="text-black" />}
              buttonColor="yellow"
              onClick={onClearFilters}>
              Limpar filtros
            </PrimaryButton>
          </div>
        </div>
      </div>
    </CascateList>
  );
}